import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  type GridProps,
  Input,
  Radio,
  Typography,
  useMediaQuery
} from '@mui/material'
import {
  includes,
  isBoolean,
  isEmpty,
  isNil,
  map,
  replace,
  split
} from 'lodash/fp'
import React from 'react'
import {
  type InputProps,
  type TextInputProps,
  useInput,
  useTranslate,
  BooleanInput as RaBooleanInput
} from 'react-admin'
import { useFormState, useFormContext } from 'react-hook-form'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import ja from 'date-fns/locale/ja'
import { format, parse, isValid, parseISO } from 'date-fns/fp'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import log from 'loglevel'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'

log.setLevel(log.levels.DEBUG)

export const ValidateError: React.FC<{
  errorMessage: string
}> = ({ errorMessage }) => {
  const translate = useTranslate()
  let message: string
  if (includes(':')(errorMessage)) {
    const items = split(':')(errorMessage)
    message = `${items[1] as string}${translate(items[0] as string)}`
  } else {
    message = translate(errorMessage)
  }
  return (
    <Typography variant="subtitle2" color="red" sx={{ marginLeft: '1em' }}>
      {message}
    </Typography>
  )
}

export const TextInput: React.FC<
  TextInputProps & {
    source: string
    placeholder: string
    fields?: string
    validate?: any
    label?: string
    subsource?: string
    onChange?: (event: any) => void
  }
> = ({ source, placeholder, validate, fields, label, subsource, ...props }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { field, isRequired } = useInput({ source, validate, ...props })
  const translate = useTranslate()
  const name = replace(/\.\d/, '')(field.name)
  const labelString = isNil(label)
    ? translate(`${fields as string}.${name}`)
    : label
  const { errors } = useFormState()
  let error: any = errors
  if (!isNil(error)) {
    for (const field of split('.')(source)) {
      error = error[field]
      if (!isNil(error) && !isNil(subsource)) {
        error = error[subsource]
      }
      if (isNil(error)) break
    }
  }
  return (
    <>
      <Typography
        variant="subtitle1"
        sx={{ marginLeft: isDesktop ? '0.50em' : '0.25em' }}>
        {labelString}
      </Typography>
      <Box
        border={1}
        sx={{
          height: '2em',
          display: 'flex',
          alignContent: 'center',
          marginTop: isDesktop ? '0.1em' : '0.25em',
          marginLeft: isDesktop ? '0.50em' : '0.25em',
          borderRadius: '10px',
          borderColor: '#c5c5c5'
        }}>
        <Input
          {...field}
          placeholder={placeholder}
          fullWidth
          disableUnderline
          sx={{ marginLeft: '1em', fontVariant: 'body1' }}
          required={isRequired}
        />
      </Box>
      {!isNil(error) && <ValidateError errorMessage={error.message} />}
    </>
  )
}
export const DateInput: React.FC<{
  source: string
  fields: string
  disableFuture?: boolean
  disablePast?: boolean
  alwaysOn?: boolean
  validate?: any
}> = ({
  source,
  disableFuture,
  disablePast,
  alwaysOn,
  validate,
  fields,
  ...props
}) => {
  const translate = useTranslate()
  const { field, isRequired } = useInput({ source, ...props })
  const { value } = field
  const [dateValue, setDateValue] = React.useState<Date | null>(
    !isNil(value) && !isEmpty(value) ? parseISO(value) : null
  )
  const { setValue } = useFormContext()
  const label = translate(`${fields}.${replace(/\.[0-9]+/g, '')(field.name)}`)
  React.useEffect(() => {
    setDateValue(!isNil(value) && !isEmpty(value) ? parseISO(value) : null)
  }, [value])
  return (
    <>
      <Typography variant="subtitle1">{label}</Typography>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={ja}
        localeText={{
          cancelButtonLabel: 'キャンセル',
          okButtonLabel: '選択'
        }}>
        <DatePicker
          value={
            isNil(dateValue) && !isNil(value) && !isEmpty(value)
              ? parseISO(value)
              : dateValue
          }
          format="yyyy-MM-dd"
          onChange={(newValue: Date | null): void => {
            if (isValid(newValue)) {
              const value = !isNil(newValue)
                ? format('yyyy-MM-dd')(newValue)
                : ''
              setDateValue(newValue)
              setValue(source, value, {
                shouldDirty: true,
                shouldValidate: true
              })
              field.onChange({ target: { value } })
            }
          }}
          slotProps={{
            field: {
              clearable: true,
              onClear: (): void => {
                setValue(source, undefined, {
                  shouldDirty: true,
                  shouldValidate: true
                })
                setDateValue(null)
                field.onChange({ target: null })
              },
              format: 'yyyy年M月d日'
            },
            textField: {
              required: isRequired,
              sx: {
                borderRadius: '10px',
                borderColor: '#c5c5c5'
              },
              variant: 'outlined',
              fullWidth: true,
              size: 'medium',
              inputProps: { style: { fontVariant: 'body1' } },
              InputLabelProps: { disabled: true },
              hiddenLabel: true
            },
            toolbar: {
              hidden: true
            }
          }}
          views={['year', 'month', 'day']}
          minDate={parse(new Date(), 'yyyy-MM-dd')('2023-01-01')}
          disableFuture={!isNil(disableFuture) ? disableFuture : false}
          disablePast={!isNil(disablePast) ? disablePast : false}
        />
      </LocalizationProvider>
    </>
  )
}
export const useScroll = (obj?: any): React.RefObject<HTMLDivElement> => {
  const scrollRef = React.useRef<HTMLDivElement>(null)
  React.useLayoutEffect(() => {
    scrollRef?.current?.scrollIntoView({
      block: 'center'
    })
  }, [])
  React.useEffect(() => {
    if (!isNil(obj)) {
      scrollRef?.current?.scrollIntoView({
        block: 'center'
      })
    }
  }, [obj])
  return scrollRef
}
export const YesNoInput: React.FC<
  InputProps & { fields: string; labels: string[] }
> = ({ fields, labels, ...props }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { source } = props
  const translate = useTranslate()
  const { field } = useInput(props)
  const { value } = field
  const [radioValue, setRadioValue] = React.useState<boolean | undefined>(
    !isBoolean(value) ? undefined : value
  )
  const { setValue } = useFormContext()
  const onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value === 'true'
    const { currentTarget } = event
    setRadioValue(value)
    setValue(source, value)
    field.onChange({
      currentTarget,
      target: { value }
    })
  }
  return (
    <Grid container>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        <Grid
          container
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
          <Grid
            item
            xs={isDesktop ? 3 : 12}
            sx={{
              display: 'flex',
              justifyContent: isDesktop ? 'right' : 'center',
              alignItems: 'center'
            }}>
            <Typography variant="body1" sx={{ mr: '0.5em' }}>
              {translate(`${fields}.${replace(/\.\d/, '')(source)}`)}
            </Typography>
          </Grid>
          <Grid item xs={isDesktop ? 1 : 6}>
            <FormControlLabel
              control={
                <Radio
                  checked={
                    (!isNil(radioValue) && radioValue) ||
                    (isBoolean(value) && value)
                  }
                  checkedIcon={<CheckBoxIcon />}
                  icon={<CheckBoxOutlineBlankIcon />}
                  onChange={onChange}
                  value="true"
                  name={source}
                />
              }
              label={labels[0]}
            />
          </Grid>
          <Grid item xs={isDesktop ? 2 : 6}>
            <FormControlLabel
              control={
                <Radio
                  checked={
                    (!isNil(radioValue) && !radioValue) ||
                    (isBoolean(value) && !value)
                  }
                  checkedIcon={<CheckBoxIcon />}
                  icon={<CheckBoxOutlineBlankIcon />}
                  onChange={onChange}
                  value="false"
                  name={source}
                />
              }
              label={labels[1]}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}
export const CheckInput: React.FC<
  InputProps & { fields: string; gridOptions?: GridProps }
> = ({ fields, gridOptions, ...props }) => {
  const { source } = props
  const translate = useTranslate()
  const { field } = useInput(props)
  const { value } = field
  const [checkedValue, setCheckedValue] = React.useState<boolean>(
    isBoolean(value) ? value : false
  )
  const { setValue } = useFormContext()
  const onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.checked ?? false
    setCheckedValue(value)
    setValue(source, value, {
      shouldDirty: true,
      shouldValidate: true
    })
    field.onChange({ target: { value } })
  }
  React.useEffect(() => {
    const { disabled } = props
    if (!isNil(disabled) && disabled) {
      setCheckedValue(false)
    }
  }, [props])
  return (
    <Grid container>
      <Grid
        item
        xs={12}
        sx={{
          display: gridOptions?.display ?? 'flex',
          justifyContent: gridOptions?.justifyContent ?? 'center',
          alignItems: gridOptions?.alignItems ?? 'center'
        }}>
        <Typography
          variant="body1"
          sx={{ mr: '0.5em' }}
          whiteSpace={'pre-line'}>
          {translate(`${fields}.${replace(/\.\d/, '')(source)}`)}
        </Typography>
        <Checkbox
          {...field}
          checked={checkedValue}
          checkedIcon={<CheckBoxIcon />}
          icon={<CheckBoxOutlineBlankIcon />}
          onChange={onChange}
          name={source}
        />
      </Grid>
    </Grid>
  )
}
export const BooleanInput: React.FC<InputProps & { fields: string }> = ({
  fields,
  ...props
}) => {
  const { source } = props
  const translate = useTranslate()
  return (
    <RaBooleanInput
      source={source}
      label={translate(`${fields}.${replace(/\.\d/, '')(source)}`)}
    />
  )
}
export const RadioInput: React.FC<
  InputProps & {
    fields: string
    labels1: string[]
    labels2?: string[]
    values: string[]
    descriptions1?: string[]
    descriptions2?: string[]
  }
> = ({
  fields,
  labels1,
  labels2,
  values,
  descriptions1,
  descriptions2,
  ...props
}) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { source } = props
  const { field } = useInput(props)
  const { value } = field
  const [radioValue, setRadioValue] = React.useState<string | undefined>(
    isEmpty(value) ? undefined : value
  )
  const { setValue } = useFormContext()
  const onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value
    const { currentTarget } = event
    setRadioValue(value)
    setValue(source, value)
    field.onChange({
      currentTarget,
      target: { value }
    })
  }
  return (
    <Grid
      container
      xs={isDesktop ? 6 : 11}
      sx={{
        display: 'grid',
        justifyContent: 'normal',
        alignItems: 'center'
      }}>
      {
        // @ts-expect-error ts(2339)
        map.convert({ cap: false })((label: string, index: number) => {
          return (
            <Grid
              item
              marginY={2}
              sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Radio
                    checked={
                      !isEmpty(radioValue) && radioValue === values[index]
                    }
                    checkedIcon={<CheckBoxIcon />}
                    icon={<CheckBoxOutlineBlankIcon />}
                    onChange={onChange}
                    value={values[index]}
                    sx={{}}
                  />
                }
                label=""
                sx={{
                  whiteSpace: 'pre-wrap',
                  mb: '-0.5em'
                }}
              />
              <Grid>
                <Grid container display="flex">
                  <Typography
                    variant="body1"
                    sx={{ 'white-space': 'pre-wrap' }}>
                    {labels1[index]}
                  </Typography>
                  <Grid
                    item
                    alignContent="end"
                    sx={{
                      ml: '-1.5em'
                    }}>
                    {!isNil(labels2) ? (
                      <Typography
                        variant="body2"
                        sx={{
                          'white-space': 'pre-wrap',
                          fontStyle: 'italic'
                        }}>
                        {labels2[index]}
                      </Typography>
                    ) : (
                      <></>
                    )}
                  </Grid>
                </Grid>
                {!isNil(descriptions1) ? (
                  <Typography
                    variant="body1"
                    sx={{
                      mt: '0.5em',
                      mr: '0.5em',
                      'white-space': 'pre-wrap',
                      fontSize: 15
                    }}>
                    {descriptions1[index]}
                  </Typography>
                ) : (
                  <></>
                )}
                {!isNil(descriptions2) ? (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: '0.5em',
                      mr: '0.5em',
                      'white-space': 'pre-wrap'
                    }}>
                    {descriptions2[index]}
                  </Typography>
                ) : (
                  <></>
                )}
              </Grid>
            </Grid>
          )
        })(labels1)
      }
    </Grid>
  )
}
